import { Component, inject, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ServicePortalApi } from '../../../api/api';
import { TokenMapperInput } from '../../../api/types';
import { SynBreadcrumbComponent, SynBreadcrumbItemComponent } from '@synergy-design-system/angular';
import { globalClientId } from '../../state';
import { TokenMapperFormComponent } from './token-mapper-form.component';
import { ChooseTokenMapperTypeComponent } from './choose-token-mapper-type.component';
import { TokenMapperType } from '../../types';

@Component({
  selector: 'app-add-token-mapper',
  template: `
    <syn-breadcrumb>
      <syn-breadcrumb-item routerLink="" i18n="@@home">Home</syn-breadcrumb-item>
      <syn-breadcrumb-item routerLink="/clients" i18n="@@clients">Clients</syn-breadcrumb-item>
      <syn-breadcrumb-item [routerLink]="['/', 'clients', clientId]">{{ globalClientId() ?? clientId }}
      </syn-breadcrumb-item>
      <syn-breadcrumb-item [routerLink]="['/', 'clients', clientId, 'mappers']">
        <span i18n="@@token-mappers">Token Mappers</span>
      </syn-breadcrumb-item>
      <syn-breadcrumb-item i18n="@@add-token-mapper">Add Token Mapper</syn-breadcrumb-item>
    </syn-breadcrumb>

    <h1 i18n="@@add-token-mapper">Add Token Mapper</h1>

    @if (mapper()) {
      <app-token-mapper-form
        [mapper]="mapper()!"
        [create]="true"
        submitIcon="save"
        submitText="Add Token Mapper"
        i18n-submitText="@@add-token-mapper"
        (submitMapper)="onSubmit($event)"
      />
    } @else {
      <app-choose-token-mapper-type (selected)="onTypeSelected($event)" />
    }
  `,
  styles: [],
  imports: [
    RouterLink,
    SynBreadcrumbComponent,
    SynBreadcrumbItemComponent,
    TokenMapperFormComponent,
    ChooseTokenMapperTypeComponent,
  ],
})
export class AddTokenMapperComponent {
  clientId = inject(ActivatedRoute).snapshot.params['id'];
  api = inject(ServicePortalApi);
  router = inject(Router);

  emptyTokenMapper = {
    displayName: '',
    multivalued: false,
    addToAccessToken: true,
    addToIdToken: true,
    addToUserInfo: true,
  } as TokenMapperInput;

  mapper: WritableSignal<TokenMapperInput | null> = signal(null);

  onTypeSelected(type: TokenMapperType) {
    const mapper = { ...this.emptyTokenMapper };
    switch (type) {
      case 'session':
        mapper.session = { claimName: '', note: '' };
        break;
      case 'userAttribute':
        mapper.userAttribute = { claimName: '', name: '' };
        break;
      case 'audience':
        mapper.multivalued = true;
        mapper.audience = { client: '' };
        break;
      case 'clientRoles':
        mapper.multivalued = true;
        mapper.clientRoles = { claimName: '', rolePrefix: '', clientId: '' };
        break;
      case 'realmRoles':
        mapper.multivalued = true;
        mapper.realmRoles = { claimName: '', rolePrefix: '' };
        break;
    }

    this.mapper.set(mapper);
  }

  onSubmit(mapper: TokenMapperInput) {
    this.api.createTokenMapper(this.clientId, mapper, (createdMapper) => {
      void this.router.navigate(['clients', this.clientId, 'mappers', createdMapper.id])
        .then(() => {
        });
    });
  }

  protected readonly globalClientId = globalClientId;
}
